import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/shared/ui/components/button';
import { Input } from '@/shared/ui/components/input';
import { Label } from '@/shared/ui/components/label';
import { useAuth } from '@/modules/auth/AuthContext';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { useVenueContext } from '@/modules/venue/VenueContext';
import { apiErrorMessage } from '@/shared/api-client/workspace';
import { useUpdateVenueBookingSettings, useVenueBookingSettings } from './bookingSettings';

/** Maps a backend error code to a translated, user-facing message. */
export function BookingSettingsPage() {
  const { t } = useTranslation('common');
  const { venues } = useAuth();
  const { venueId } = useVenueContext();
  const auth = useAuthorization();
  const canManage = auth.can('venue.booking_settings.manage', { kind: 'venue', venueId });
  const venueName = venues.find((venue) => venue.id === venueId)?.name ?? venueId;

  const { data, isLoading, isError } = useVenueBookingSettings(venueId);
  const update = useUpdateVenueBookingSettings(venueId);

  const [bookingHorizonDays, setBookingHorizonDays] = useState('');
  const [cancellationCutoffMinutes, setCancellationCutoffMinutes] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setBookingHorizonDays(String(data.bookingHorizonDays));
      setCancellationCutoffMinutes(String(data.cancellationCutoffMinutes));
    }
  }, [data]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    const horizon = Number(bookingHorizonDays);
    const cutoff = Number(cancellationCutoffMinutes);
    if (!Number.isInteger(horizon) || horizon < 1) {
      setError(t('venues.bookingSettings.errors.invalid_booking_horizon_days'));
      return;
    }
    if (!Number.isInteger(cutoff) || cutoff < 1) {
      setError(t('venues.bookingSettings.errors.invalid_cancellation_cutoff_minutes'));
      return;
    }
    try {
      await update.mutateAsync({
        bookingHorizonDays: horizon,
        cancellationCutoffMinutes: cutoff,
      });
      toast.success(t('venues.bookingSettings.toast.updated'));
    } catch (caught) {
      setError(apiErrorMessage(t, 'venues.bookingSettings', caught));
    }
  }

  return (
    <section className="grid gap-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">{t('venues.bookingSettings.pageTitle')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('venues.bookingSettings.scopedDescription', { name: venueName })}
        </p>
      </div>

      {isLoading && <p className="text-muted-foreground">{t('venues.bookingSettings.loading')}</p>}
      {isError && (
        <p className="text-destructive">{t('venues.bookingSettings.errors.loadFailed')}</p>
      )}

      {data && (
        <form onSubmit={submit} className="grid max-w-md gap-4">
          <div className="grid gap-2">
            <Label htmlFor="booking-horizon-days">
              {t('venues.bookingSettings.fields.bookingHorizonDays')}
            </Label>
            <Input
              id="booking-horizon-days"
              type="number"
              required
              disabled={!canManage}
              value={bookingHorizonDays}
              onChange={(event) => setBookingHorizonDays(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {t('venues.bookingSettings.fields.bookingHorizonDaysHint')}
            </p>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="cancellation-cutoff-minutes">
              {t('venues.bookingSettings.fields.cancellationCutoffMinutes')}
            </Label>
            <Input
              id="cancellation-cutoff-minutes"
              type="number"
              required
              disabled={!canManage}
              value={cancellationCutoffMinutes}
              onChange={(event) => setCancellationCutoffMinutes(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {t('venues.bookingSettings.fields.cancellationCutoffMinutesHint')}
            </p>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          {canManage && (
            <div>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending
                  ? t('venues.bookingSettings.form.submitting')
                  : t('venues.bookingSettings.form.save')}
              </Button>
            </div>
          )}
        </form>
      )}
    </section>
  );
}
